import { useMemo } from 'react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { EventSlotDto } from '@/types/dtos/events/response/event.dto';
import { usePublicationStateQuery } from '@/state/queries/events/use-publication-state';
import { PUBLICATION_ITEM_TYPE } from '@/types/dtos/events/request/batch-publish.dto';
import { getObjectId } from '@/helpers/object.id.helper';
import type { PublicationStatePhase } from './use-flow-publish-state-for-flow-detail';

export type FlowBasicsSlotPublishState = {
  publicationPhase: PublicationStatePhase;
  isUpToDate: boolean;
  needsInitialPublish: boolean;
  needsRepublish: boolean;
  flowBasicsUpToDate: boolean;
  eventSlotUpToDate: boolean;
};

function eventSlotPublicationScopeKey(planEventId: string, planSlotId: string): string {
  return `${planEventId}:eventSlot:${planSlotId}`;
}

/**
 * Combined publication state for Flow Basics (`FLOW_BASIC`) + the selected plan event slot (`EVENT_SLOT`).
 * Staleness from GET publication-state `isUpToDate` only when `publicationPhase === 'ready'`.
 * Pass `enabled` only when the user may update the event (same as batch publish).
 */
export function useFlowBasicsSlotPublishState(options: {
  enabled: boolean;
  planEventId: string | undefined;
  planFlowId: string | null;
  flowPlan: FlowDto | null;
  planEventSlot: EventSlotDto | null | undefined;
}): FlowBasicsSlotPublishState | null {
  const { enabled, planEventId, planFlowId, flowPlan, planEventSlot } = options;

  const publicationQuery = usePublicationStateQuery({
    eventId: enabled && planEventId ? planEventId : undefined,
    mode: 'ORGANIZE',
    enabled,
  });

  const publicationPhase: PublicationStatePhase = useMemo(() => {
    if (!enabled) return 'disabled';
    if (publicationQuery.isError) return 'error';
    if (publicationQuery.isPublicationStateReady) return 'ready';
    return 'loading';
  }, [enabled, publicationQuery.isError, publicationQuery.isPublicationStateReady]);

  /** Backend stores EVENT_SLOT keys with the resolved PLAN event id; the store may only have the organize child id. */
  const resolvedPlanEventIdForScope = publicationQuery.data?.planEventId ?? planEventId;

  const slotScopeKey =
    resolvedPlanEventIdForScope && planEventSlot?.id
      ? eventSlotPublicationScopeKey(resolvedPlanEventIdForScope, planEventSlot.id)
      : null;

  const flowBasicRecord = useMemo(() => {
    if (!publicationQuery.data || !planFlowId || !flowPlan) return undefined;
    const originId = flowPlan.origin ? getObjectId(flowPlan.origin) : flowPlan.id;
    return publicationQuery.data.items.find((i) => {
      if (i.itemType !== PUBLICATION_ITEM_TYPE.FLOW_BASIC) return false;
      return i.scopeKey === planFlowId || i.scopeKey === flowPlan.id || i.scopeKey === originId;
    });
  }, [publicationQuery.data, planFlowId, flowPlan]);

  const eventSlotRecord = useMemo(() => {
    if (!publicationQuery.data || !slotScopeKey) return undefined;
    return publicationQuery.data.items.find(
      (i) => i.itemType === PUBLICATION_ITEM_TYPE.EVENT_SLOT && i.scopeKey === slotScopeKey,
    );
  }, [publicationQuery.data, slotScopeKey]);

  const flowBasicsUpToDate = useMemo(() => {
    if (!flowPlan || !planFlowId) return false;
    if (publicationPhase !== 'ready') return false;
    return flowBasicRecord?.isUpToDate === true;
  }, [flowPlan, planFlowId, publicationPhase, flowBasicRecord]);

  const eventSlotUpToDate = useMemo(() => {
    if (!planEventSlot) return true;
    if (publicationPhase !== 'ready' || !slotScopeKey) return false;
    return eventSlotRecord?.isUpToDate === true;
  }, [planEventSlot, publicationPhase, slotScopeKey, eventSlotRecord]);

  return useMemo(() => {
    if (!enabled || !flowPlan || !planFlowId) {
      return null;
    }

    const isUpToDate = flowBasicsUpToDate && eventSlotUpToDate;
    const needsInitialPublish =
      publicationPhase === 'ready' ? (planEventSlot ? !flowBasicRecord && !eventSlotRecord : !flowBasicRecord) : false;
    const needsRepublish = publicationPhase === 'ready' && !needsInitialPublish && !isUpToDate;

    return {
      publicationPhase,
      isUpToDate,
      needsInitialPublish,
      needsRepublish,
      flowBasicsUpToDate,
      eventSlotUpToDate,
    };
  }, [
    enabled,
    flowPlan,
    planFlowId,
    planEventSlot,
    flowBasicsUpToDate,
    eventSlotUpToDate,
    publicationPhase,
    flowBasicRecord,
    eventSlotRecord,
  ]);
}
